import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Plus, X, Calendar, Clock } from 'lucide-react';
import { DASH, API, authHeader } from '../api';
import { SectionCard, Spinner, EmptyState, Badge, GhostButton, DangerButton, fmtMoney } from '../ui';
import ConfirmDialog from '../../components/ConfirmDialog';

const STATUS_TONE = { paid: 'green', completed: 'gray', canceled: 'red' };

/* ── Tiny helpers ─────────────────────────────────────────────── */
const today = () => new Date().toISOString().slice(0, 10);

const PAYMENT_TYPES = [
  { value: 'cash',   label: 'Cash' },
  { value: 'card',   label: 'Card (in-person)' },
  { value: 'comp',   label: 'Complimentary' },
  { value: 'credit', label: 'Session Credit' },
];

/* ── Manual Booking Modal ─────────────────────────────────────── */
const ManualBookingModal = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({
    first_name: '', email: '', phone: '',
    booking_date: today(), slot: '',
    payment_type: 'cash', amount: '', notes: '',
  });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  /* Fetch available slots when date changes */
  useEffect(() => {
    if (!form.booking_date) return;
    setLoadingSlots(true);
    setForm(f => ({ ...f, slot: '' }));
    axios.get(`${API}/bookings/availability?date=${form.booking_date}`)
      .then(r => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [form.booking_date]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.first_name.trim()) return setError('Customer name is required.');
    if (!form.slot) return setError('Please select a time slot.');
    setSaving(true);
    try {
      await axios.post(`${DASH}/bookings/manual`, {
        ...form,
        amount: parseFloat(form.amount) || 0,
      }, { headers: authHeader() });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not create booking.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full rounded-xl border border-[#e8e0d0] px-3 py-2 text-sm text-[#0d1f15] focus:outline-none focus:border-[#1f3d2b] bg-white';
  const labelCls = 'block text-xs font-bold uppercase tracking-[0.15em] text-[#5a7062] mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm px-0 sm:px-4">
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-[#f0ebe0]">
          <div>
            <h3 className="text-lg font-bold text-[#0d1f15]" style={{ fontFamily: "'Manrope', sans-serif" }}>
              New Manual Booking
            </h3>
            <p className="text-xs text-[#5a7062] mt-0.5">Walk-in, cash, or phone booking</p>
          </div>
          <button
            data-testid="manual-booking-close"
            onClick={onClose}
            className="p-2 rounded-full text-[#5a7062] hover:bg-[#f0ebe0] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

          {/* Customer info */}
          <div>
            <label className={labelCls}>Customer Name *</label>
            <input
              data-testid="manual-booking-name"
              className={inputCls}
              placeholder="First name"
              value={form.first_name}
              onChange={e => set('first_name', e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Email</label>
              <input
                data-testid="manual-booking-email"
                className={inputCls}
                type="email"
                placeholder="optional"
                value={form.email}
                onChange={e => set('email', e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input
                data-testid="manual-booking-phone"
                className={inputCls}
                placeholder="optional"
                value={form.phone}
                onChange={e => set('phone', e.target.value)}
              />
            </div>
          </div>

          {/* Date + Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}><Calendar size={10} className="inline mr-1" />Date *</label>
              <input
                data-testid="manual-booking-date"
                className={inputCls}
                type="date"
                min={today()}
                value={form.booking_date}
                onChange={e => set('booking_date', e.target.value)}
                required
              />
            </div>
            <div>
              <label className={labelCls}><Clock size={10} className="inline mr-1" />Time *</label>
              {loadingSlots ? (
                <div className="text-xs text-[#5a7062] pt-2">Loading…</div>
              ) : (
                <select
                  data-testid="manual-booking-slot"
                  className={inputCls}
                  value={form.slot}
                  onChange={e => set('slot', e.target.value)}
                  required
                >
                  <option value="">— pick time —</option>
                  {slots.map(s => (
                    <option
                      key={s.slot}
                      value={s.slot}
                      disabled={!s.available}
                    >
                      {s.label}{!s.available ? ' (taken)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Payment */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Payment Type</label>
              <select
                data-testid="manual-booking-payment-type"
                className={inputCls}
                value={form.payment_type}
                onChange={e => set('payment_type', e.target.value)}
              >
                {PAYMENT_TYPES.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Amount ($)</label>
              <input
                data-testid="manual-booking-amount"
                className={inputCls}
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={form.amount}
                onChange={e => set('amount', e.target.value)}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className={labelCls}>Notes</label>
            <textarea
              data-testid="manual-booking-notes"
              className={`${inputCls} resize-none`}
              rows={2}
              placeholder="e.g. Walk-in, referred by…"
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</div>
          )}
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#f0ebe0] flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-[#e8e0d0] py-2.5 text-sm font-semibold text-[#5a7062] hover:bg-[#f6f4ef] transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="manual-booking-submit"
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 rounded-xl py-2.5 text-sm font-bold text-white transition-all disabled:opacity-60"
            style={{ background: '#1f3d2b' }}
          >
            {saving ? 'Saving…' : 'Create Booking'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main Section ─────────────────────────────────────────────── */
const BookingsSection = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${DASH}/bookings`, { headers: authHeader() })
      .then((res) => setRows(res.data || []))
      .catch(() => setError('Could not load bookings.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAction = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await axios.post(`${DASH}/bookings/${confirm.id}/${confirm.action}`, {}, { headers: authHeader() });
      setConfirm(null);
      load();
    } catch {
      setError('Action failed — please retry.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner label="Loading bookings…" />;

  return (
    <div data-testid="wellness-admin-section-bookings" className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl tracking-tight" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, color: '#0d1f15' }}>
            Bookings
          </h2>
          <p className="text-sm text-[#5a7062] mt-1">Next 30 days of appointments.</p>
        </div>
        <button
          data-testid="new-manual-booking-btn"
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 whitespace-nowrap flex-shrink-0"
          style={{ background: '#1f3d2b' }}
        >
          <Plus size={15} strokeWidth={2.5} /> New Booking
        </button>
      </div>

      {error && <div className="text-sm text-red-700">{error}</div>}

      <SectionCard testId="bookings-table-card">
        {rows.length === 0 ? (
          <EmptyState testId="bookings-empty" label="No upcoming bookings." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-[#5a7062] border-b border-[#f0ebe0]">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Time</th>
                  <th className="py-2 pr-4">Customer</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Amount</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => (
                  <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-[#f6f4ef] last:border-0">
                    <td className="py-3 pr-4 whitespace-nowrap">{b.booking_date}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">{b.slot_label || b.slot}</td>
                    <td className="py-3 pr-4">
                      <div className="font-semibold text-[#0d1f15]">{b.first_name}</div>
                      <div className="text-xs text-[#5a7062]">{b.email}</div>
                    </td>
                    <td className="py-3 pr-4 capitalize">
                      {b.manual ? (
                        <span className="inline-flex items-center gap-1">
                          {b.payment_type}
                          <span className="text-[10px] font-bold uppercase tracking-wide text-[#d97736] bg-[#d97736]/10 px-1.5 py-0.5 rounded-full">manual</span>
                        </span>
                      ) : b.payment_type}
                    </td>
                    <td className="py-3 pr-4">{fmtMoney(b.amount)}</td>
                    <td className="py-3 pr-4"><Badge tone={STATUS_TONE[b.status] || 'gray'}>{b.status}</Badge></td>
                    <td className="py-3 pr-4">
                      <div className="flex justify-end gap-2">
                        {b.status === 'paid' && (
                          <>
                            <GhostButton testId={`booking-complete-${b.id}`} onClick={() => setConfirm({ id: b.id, action: 'complete' })}>
                              <CheckCircle2 size={14} /> Complete
                            </GhostButton>
                            <DangerButton testId={`booking-cancel-${b.id}`} onClick={() => setConfirm({ id: b.id, action: 'cancel' })}>
                              <XCircle size={14} /> Cancel
                            </DangerButton>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {showModal && (
        <ManualBookingModal
          onClose={() => setShowModal(false)}
          onSaved={load}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.action === 'cancel' ? 'Cancel this booking?' : 'Mark as complete?'}
        message={confirm?.action === 'cancel' ? 'The customer will lose this slot. If it was paid with a credit, the credit is refunded automatically.' : 'This marks the session as delivered.'}
        confirmLabel={confirm?.action === 'cancel' ? 'Cancel Booking' : 'Mark Complete'}
        accent={confirm?.action === 'cancel' ? 'danger' : 'success'}
        busy={busy}
        onConfirm={runAction}
        onClose={() => setConfirm(null)}
      />
    </div>
  );
};

export default BookingsSection;
